import React, { useEffect, useState } from 'react';
import { OTPInput, OTPInputContext } from 'input-otp';
import { Loader2 } from 'lucide-react';

/**
 * قطعه‌های مشترک فرم‌های ورود و ثبت‌نام.
 */

/** ارقام فارسی/عربی به لاتین — کاربر ایرانی معمولاً با کیبورد فارسی تایپ می‌کند */
export const toLatinDigits = (value: string) =>
    value
        .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
        .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));

/** ۰۹۱۲۱۲۳۴۵۶۷ ← +98 912 123 4567 / 912… */
export const normalizePhone = (raw: string) => {
    let s = toLatinDigits(raw).replace(/[^\d]/g, '');
    if (s.startsWith('98') && s.length === 12) s = `0${s.slice(2)}`;
    if (s.length === 10 && s.startsWith('9')) s = `0${s}`;
    return s;
};

export const isValidPhone = (phone: string) => /^09\d{9}$/.test(phone);

/** ۰۹۱۲۱۲۳۴۵۶۷ → ۰۹۱۲ ••• ۴۵۶۷ */
export const maskPhone = (phone: string) =>
    phone.length === 11 ? `${phone.slice(0, 4)} ••• ${phone.slice(7)}` : phone;

/* --------------------------------------------------------------- فیلد */

interface FieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label: string;
    hint?: string;
    optional?: boolean;
    icon?: React.ReactNode;
    error?: string;
}

export const Field = React.forwardRef<HTMLInputElement, FieldProps>(
    ({ label, hint, optional, icon, error, className = '', ...props }, ref) => (
        <label className="block">
            <span className="mb-2 flex items-center justify-between text-sm font-medium text-[#0B1F19]">
                {label}
                {optional && <span className="text-xs font-normal text-stone-400">اختیاری</span>}
            </span>
            <span className="relative block">
                {icon && (
                    <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-stone-400">
                        {icon}
                    </span>
                )}
                <input
                    ref={ref}
                    {...props}
                    className={`h-12 w-full rounded-2xl border bg-white px-4 text-[15px] text-[#0B1F19] outline-none transition placeholder:text-stone-300 focus:border-emerald-600 focus:ring-4 focus:ring-emerald-600/10 ${
                        icon ? 'pr-11' : ''
                    } ${error ? 'border-red-400' : 'border-stone-200'} ${className}`}
                />
            </span>
            {error ? (
                <span className="mt-1.5 block text-xs text-red-600">{error}</span>
            ) : hint ? (
                <span className="mt-1.5 block text-xs text-stone-400">{hint}</span>
            ) : null}
        </label>
    )
);
Field.displayName = 'Field';

/* ----------------------------------------------------------- دکمه‌ی اصلی */

export const PrimaryButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { loading?: boolean }> = ({
    loading,
    children,
    disabled,
    className = '',
    ...props
}) => (
    <button
        {...props}
        disabled={disabled || loading}
        className={`group relative flex h-12 w-full items-center justify-center gap-2 overflow-hidden rounded-2xl bg-[#06281F] font-semibold text-white transition hover:bg-[#0A3A2D] disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
    >
        <span
            aria-hidden
            className="absolute inset-0 -translate-x-full bg-gradient-to-l from-transparent via-emerald-400/25 to-transparent transition-transform duration-700 group-hover:translate-x-full"
        />
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        <span className="relative">{children}</span>
    </button>
);

/* --------------------------------------------------------------- خطا */

export const ErrorNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm leading-7 text-red-700">
        {children}
    </div>
);

/* ------------------------------------------------------- خانه‌های کد */

export const OtpBoxes: React.FC<{
    value: string;
    onChange: (v: string) => void;
    onComplete?: (v: string) => void;
    disabled?: boolean;
    invalid?: boolean;
}> = ({ value, onChange, onComplete, disabled, invalid }) => (
    // کد از چپ به راست خوانده می‌شود، حتی در صفحه‌ی راست‌به‌چپ
    <div dir="ltr" className="flex justify-center">
        <OTPInput
            maxLength={6}
            value={value}
            onChange={(v) => onChange(toLatinDigits(v).replace(/\D/g, ''))}
            onComplete={onComplete}
            disabled={disabled}
            autoFocus
            inputMode="numeric"
            autoComplete="one-time-code"
            containerClassName="flex gap-2 sm:gap-3"
            render={({ slots }) => (
                <>
                    {slots.map((slot, i) => (
                        <div
                            key={i}
                            className={`relative flex h-14 w-11 items-center justify-center rounded-2xl border bg-white text-xl font-bold text-[#0B1F19] transition sm:w-12 ${
                                invalid
                                    ? 'border-red-400'
                                    : slot.isActive
                                      ? 'border-emerald-600 ring-4 ring-emerald-600/10'
                                      : slot.char
                                        ? 'border-emerald-300'
                                        : 'border-stone-200'
                            }`}
                        >
                            {slot.char}
                            {slot.hasFakeCaret && (
                                <span className="absolute h-6 w-px animate-pulse bg-emerald-700" />
                            )}
                        </div>
                    ))}
                </>
            )}
        />
    </div>
);

// برای سازگاری با wrapper موجود در components/ui
export { OTPInputContext };

/* ------------------------------------------------------ شمارش معکوس */

export function useCountdown(seconds: number) {
    const [left, setLeft] = useState(seconds);

    useEffect(() => {
        if (left <= 0) return;
        const t = setTimeout(() => setLeft((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [left]);

    return { left, restart: () => setLeft(seconds) };
}

export const fa = (n: number) => n.toLocaleString('fa-IR');
